import clsx from 'clsx';
import { useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { Icon, type IconName } from './Icon';
import styles from './Field.module.css';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  hint?: string;
  error?: string | null;
  leadingIcon?: IconName;
  trailing?: ReactNode;
  compact?: boolean;
}

export function TextField({
  label,
  hint,
  error,
  leadingIcon,
  trailing,
  compact = false,
  className,
  id,
  ...rest
}: TextFieldProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;

  return (
    <div className={styles.field}>
      {label ? (
        <label className={styles.label} htmlFor={inputId}>
          {label}
        </label>
      ) : null}

      <div className={styles.control}>
        {leadingIcon ? (
          <span className={styles.leadingIcon}>
            <Icon name={leadingIcon} size={16} />
          </span>
        ) : null}

        <input
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={clsx(
            styles.input,
            leadingIcon && styles.withLeadingIcon,
            trailing && styles.withTrailing,
            error && styles.invalid,
            compact && styles.search,
            className,
          )}
          {...rest}
        />

        {trailing ? <span className={styles.trailing}>{trailing}</span> : null}
      </div>

      {error ? (
        <span className={styles.error} id={`${inputId}-error`} role="alert">
          <Icon name="alert" size={13} />
          {error}
        </span>
      ) : hint ? (
        <span className={styles.hint} id={`${inputId}-hint`}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}
